/**
 * A scan flattened into the elements a person selects and measures.
 *
 * This is the model behind the scan viewer: every wall, window, door, opening,
 * floor and object as one addressable list, each carrying its own dimensions
 * and — for walls — the openings cut into it.
 *
 * It lives in `@sts/geometry` rather than in the portal because the same
 * numbers have to appear in three places and must not disagree: the viewer on
 * screen, the survey report a designer downloads, and anything the API derives.
 * A wall that reads 8.52 m² in the browser and 9.0 m² in a PDF is worse than
 * either number alone.
 *
 * PROVENANCE. Every figure here comes from LiDAR surface extents: accurate to a
 * few centimetres, and only as complete as the surfaces the scan actually saw.
 * They are survey observations, not design inputs — anything a heat loss
 * calculation depends on needs a measured value with its own provenance.
 */
import type {
  CapturedObject,
  CapturedRoom,
  CapturedSurface,
  RoomPlanCapture,
} from "./roomplan.js";
import { planPolygonAreaM2 } from "./plan-measure.js";
import { capturedRoomRef, capturedRooms } from "./roomplan.js";
import { splitFloorOutline } from "./room-split.js";
import { storyLabel } from "./storey-name.js";

export type ScanElementKind =
  | "wall"
  | "window"
  | "door"
  | "opening"
  | "floor"
  | "object";

export interface ScanElement {
  readonly id: string;
  readonly kind: ScanElementKind;
  /** "Wall 3", "Window 1", "Chair" — what a person reads in a list. */
  readonly label: string;
  /** Index of the room this belongs to, for grouping. */
  readonly roomIndex: number;
  readonly roomLabel: string;
  readonly story: number;
  readonly widthM: number;
  readonly heightM: number;
  /** Objects only: their third dimension. Surfaces are planes and have none. */
  readonly depthM?: number;
  readonly areaM2: number;
  /** Openings cut into this element. Non-zero only for walls. */
  readonly openingsAreaM2: number;
  readonly openingIds: readonly string[];
  /** Area less its openings, floored at zero. */
  readonly netAreaM2: number;
  /** The wall this window or door sits in, when RoomPlan recorded one. */
  readonly parentId?: string;
  readonly transform?: readonly number[];
  readonly dimensions?: readonly number[];
  /**
   * A wall's thickness, metres — only when the designer has SET one in the
   * plan's edit layer (`plan-edits.ts`). RoomPlan's own `dimensions[2]` is not
   * read as a thickness anywhere, so an unset thickness stays unknown.
   */
  readonly thicknessM?: number;
  /**
   * A floor's outline on the plan, metres (x = world X, y = world Z) — only
   * for a floor the plan's edit layer REDREW to the corrected walls. A
   * scanned floor has none and is drawn from its dimensions, as always.
   */
  readonly planOutline?: readonly { readonly x: number; readonly y: number }[];
}

/**
 * What a capture has no field for, supplied by the plan's edit layer
 * (`applyPlanEdits`): the names elements keep when others around them are
 * deleted or added, the names designers gave rooms, and walls' thicknesses.
 * Every map is optional; with none, `scanRoomGroups` names and measures exactly
 * as it always has.
 */
export interface ScanGroupOverrides {
  /** Element id -> "Wall 3", "New door 1". */
  readonly elementLabels?: ReadonlyMap<string, string>;
  /** `capturedRoomRef` -> "Kitchen". */
  readonly roomLabels?: ReadonlyMap<string, string>;
  /** Wall id -> thickness, metres. */
  readonly thicknessM?: ReadonlyMap<string, number>;
  /** Floor id -> its outline, for a floor redrawn to corrected walls. Its area is the outline's. */
  readonly floorOutlines?: ReadonlyMap<string, readonly { readonly x: number; readonly y: number }[]>;
  /**
   * Storey level -> a name someone typed for it. Wins over the generated
   * "Ground floor" / "First floor" (`storyLabel`); a blank one does not.
   */
  readonly storeyLabels?: ReadonlyMap<number, string>;
}

export interface ScanRoomGroup {
  readonly index: number;
  /**
   * The room's identity — `capturedRoomRef`, the same value `convertCapture`
   * stores as the survey room's `externalRef`. What joins a scanned room to
   * the heat-loss room it became. Always set by `scanRoomGroups`; optional
   * only so hand-built groups (tests, fixtures) need not invent one.
   */
  readonly externalRef?: string;
  readonly label: string;
  readonly story: number;
  readonly storyLabel: string;
  readonly elements: readonly ScanElement[];
  readonly floorAreaM2?: number;
  /** Tallest wall. RoomPlan captures no ceiling, so this is the best available
   * floor-to-ceiling figure — and it is labelled as derived, not measured. */
  readonly ceilingHeightM?: number;
  readonly wallAreaM2: number;
  readonly openingsAreaM2: number;
  readonly netWallAreaM2: number;
}

/** The storey naming rule lives in `storey-name.ts`; re-exported here, where it always was. */
export { storyLabel };

function area(surface: CapturedSurface): number {
  const [width, height] = surface.dimensions;
  if (typeof width !== "number" || typeof height !== "number") return 0;
  return width * height;
}

/**
 * Every element of a capture, grouped by room.
 *
 * Openings are attributed to a wall through `parentIdentifier`, which RoomPlan
 * sets on each window and door. Attributing an opening to every wall — or to
 * none — would misstate the fabric area of the whole room, so the link is used
 * rather than guessed at from geometry.
 */
export function scanRoomGroups(
  capture: RoomPlanCapture,
  overrides: ScanGroupOverrides = {},
): readonly ScanRoomGroup[] {
  return capturedRooms(capture).map((room, roomIndex) => {
    const externalRef = capturedRoomRef(room, roomIndex);
    const roomLabel = overrides.roomLabels?.get(externalRef) ?? `Room ${roomIndex + 1}`;
    const story = room.story ?? 0;
    const openings = [
      ...(room.windows ?? []),
      ...(room.doors ?? []),
      ...(room.openings ?? []),
    ];

    const elements: ScanElement[] = [];

    const addSurfaces = (
      surfaces: readonly CapturedSurface[] | undefined,
      kind: ScanElementKind,
      singular: string,
      numbered: boolean,
    ): void => {
      (surfaces ?? []).forEach((surface, index) => {
        const [width = 0, height = 0] = surface.dimensions;
        const mine =
          kind === "wall"
            ? openings.filter((o) => o.parentIdentifier === surface.identifier)
            : [];
        const openingsAreaM2 = mine.reduce((sum, o) => sum + area(o), 0);
        const outline =
          kind === "floor"
            ? (overrides.floorOutlines?.get(surface.identifier) ?? splitFloorOutline(surface))
            : undefined;
        const areaM2 = outline ? planPolygonAreaM2(outline) : width * height;
        const thicknessM = overrides.thicknessM?.get(surface.identifier);
        elements.push({
          id: surface.identifier,
          kind,
          label:
            overrides.elementLabels?.get(surface.identifier) ??
            (numbered ? `${singular} ${index + 1}` : singular),
          roomIndex,
          roomLabel,
          story: surface.story ?? story,
          widthM: width,
          heightM: height,
          areaM2,
          openingsAreaM2,
          openingIds: mine.map((o) => o.identifier),
          netAreaM2: Math.max(areaM2 - openingsAreaM2, 0),
          ...(surface.parentIdentifier
            ? { parentId: surface.parentIdentifier }
            : {}),
          transform: surface.transform,
          dimensions: surface.dimensions,
          ...(thicknessM !== undefined ? { thicknessM } : {}),
          ...(outline ? { planOutline: outline } : {}),
        });
      });
    };

    addSurfaces(room.walls, "wall", "Wall", true);
    addSurfaces(room.windows, "window", "Window", true);
    addSurfaces(room.doors, "door", "Door", true);
    addSurfaces(room.openings, "opening", "Opening", true);
    addSurfaces(room.floors, "floor", "Floor", true);

    (room.objects ?? []).forEach((object: CapturedObject) => {
      const [width = 0, height = 0, depth = 0] = object.dimensions ?? [];
      elements.push({
        id: object.identifier,
        kind: "object",
        // The category IS the useful name — "Chair", "Table" — and a UUID is
        // not. Falling back to "Object" keeps an unclassified item selectable.
        label: object.category
          ? object.category.charAt(0).toUpperCase() + object.category.slice(1)
          : "Object",
        roomIndex,
        roomLabel,
        story: object.story ?? story,
        widthM: width,
        heightM: height,
        depthM: depth,
        areaM2: width * depth,
        openingsAreaM2: 0,
        openingIds: [],
        netAreaM2: width * depth,
        transform: object.transform,
        dimensions: object.dimensions,
      });
    });

    const walls = elements.filter((e) => e.kind === "wall");
    const wallAreaM2 = walls.reduce((sum, w) => sum + w.areaM2, 0);
    const openingsAreaM2 = walls.reduce((sum, w) => sum + w.openingsAreaM2, 0);
    const floors = elements.filter((e) => e.kind === "floor").reduce((sum, f) => sum + f.areaM2, 0);
    const ceilingHeights = walls.map((w) => w.heightM).filter((h) => h > 0);

    return {
      index: roomIndex,
      externalRef,
      label: roomLabel,
      story,
      storyLabel: storyLabel(story, overrides.storeyLabels?.get(story)),
      elements,
      // A room whose floor the scan missed reports NOTHING, not zero: "no
      // floor was captured" and "this room has no floor" are different claims.
      ...(floors > 0 ? { floorAreaM2: floors } : {}),
      ...(ceilingHeights.length > 0
        ? { ceilingHeightM: Math.max(...ceilingHeights) }
        : {}),
      wallAreaM2,
      openingsAreaM2,
      netWallAreaM2: Math.max(wallAreaM2 - openingsAreaM2, 0),
    };
  });
}

export interface ScanTotals {
  readonly rooms: number;
  readonly walls: number;
  readonly windows: number;
  readonly doors: number;
  readonly objects: number;
  readonly floorAreaM2?: number;
  readonly netWallAreaM2: number;
}

export function scanTotals(groups: readonly ScanRoomGroup[]): ScanTotals {
  const count = (kind: ScanElementKind): number =>
    groups.reduce(
      (sum, g) => sum + g.elements.filter((e) => e.kind === kind).length,
      0,
    );
  const floorAreas = groups
    .map((g) => g.floorAreaM2)
    .filter((a): a is number => typeof a === "number");

  return {
    rooms: groups.length,
    walls: count("wall"),
    windows: count("window"),
    doors: count("door"),
    objects: count("object"),
    ...(floorAreas.length > 0
      ? { floorAreaM2: floorAreas.reduce((a, b) => a + b, 0) }
      : {}),
    netWallAreaM2: groups.reduce((sum, g) => sum + g.netWallAreaM2, 0),
  };
}
